import { fetchItems, postItem } from './queries';
import { Opportunity, OpportunitySEStatus } from '../types/opportunity';
import { store } from './store';
import { createSEActivity } from './salesForceHelpers';
import { SalesforceTaskResponse, SfRecordType } from '../types/salesforce';


// Helper function to delay execution
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Function to check if the user is in the opportunity team
export const isUserInOpportunityTeam = async (opportunityId: string, userId: string) => {
  try {
    const response = await fetchItems('sf/query', {
      q: `SELECT Id FROM OpportunityTeamMember WHERE OpportunityId = '${opportunityId}' AND UserId = '${userId}'`
    });
    return response.length > 0;
  } catch (error) {
    console.error('Error checking user in opportunity team:', error);
    return false;
  }
};

// Main function to tag user and poll for confirmation
export const tagUserToOpportunity = async (opp: Opportunity) => {
  try {
    const state = store.getState();
    const userId = state.user.userData.sfId;
    const updated_opp = {
      "OpportunityId": opp?.id ?? opp?.Id,
      "UserId": userId,
      "Action": "Add"
    };

    const response = await postItem(`sf/flow/SecurityToolboxAddSEToOpportunity`, updated_opp);
    if (!response) {
      console.error(`Error tagging user`);
      return;
    }

    // Polling logic to check if the user is in the opportunity team
    const maxRetries = 20; // Maximum number of retries (10 seconds total)
    let retries = 0;
    while (retries < maxRetries) {
      const isInTeam = await isUserInOpportunityTeam((opp?.id ?? opp?.Id), userId);
      if (isInTeam) {
        await delay(4000)
        return !!response?.length;
      }
      await delay(500); // Wait for 0.5 seconds before retrying
      retries++;
    }

  } catch (error) {
    console.error('Error creating opportunity capability:', error);
  }
};

export const untagUserToOpportunity = async (oppId: string) => {
  try {
    const state = store.getState();
    const userId = state.user.userData.sfId;
    const updated_opp = {
      "OpportunityId": oppId,
      "UserId": userId,
      "Action": "Remove"
    };

    const response = await postItem(`sf/flow/SecurityToolboxAddSEToOpportunity`, updated_opp);
    if (!response) {
      console.error(`Error tagging user`);
      return;
    }
  } catch (error) {
    console.error('Error untagging user to opportunity', error);
  }
};


export const checkAndTagSE = async (opp: Opportunity) => {
  try {
    const state = store.getState();
    const userId = state.user.userData.sfId;
    const seTagged = await isUserInOpportunityTeam((opp.id ?? opp.Id), userId);
    if (!seTagged) {
      await tagUserToOpportunity(opp);
    }
    return true;
  } catch (error) {
    console.error('Error checking user in opportunity:', error);
  }
}


export const setSENotRequired = async (opp: Opportunity) => {
  try {
    const state = store.getState();
    const userId = state.user.userData.sfId;
    const updated_opp = {
      "OpportunityId": opp.id ?? opp.Id,
      "UserId": userId
    };

    const response = await postItem(`sf/flow/SecurityToolboxNoSERequired`, updated_opp);
    if (!response) {
      console.error(`Error tagging user`);
      return;
    }
  } catch (error) {
    console.error('Error untagging user to opportunity', error);
  }
};

const getSEStatus = (opportunity: Opportunity) => {
  switch (opportunity.se_phase) {
    case 'present': return 'Demo - Security';
    case 'design': return 'Design/Whiteboard';
    case 'validate': return 'Customer Workshop';
    case 'tech_close': return 'Adoption/Deployment';
    case 'discovery': return 'Portfolio Engagement';
    default: return '';
  }
};

export interface CreateSEActivityArgs {
  summary: string;
  details: string;
  serviceType?: string | null;
  technology?: string;
  dueDate?: string;
}

export const createSEActivityForOpportunity = async (
  opportunity: Opportunity,
  args: CreateSEActivityArgs
): Promise<SalesforceTaskResponse> => {
  const { serviceType = null } = args;
  const seServiceType = serviceType ?? getSEStatus(opportunity);
  const id = opportunity.id || opportunity.Id;
  return await createSEActivity(SfRecordType.Opportunity, id, {
    ...args,
    serviceType: seServiceType,
  });
};

export const getOpportunityStatusColor = (status: OpportunitySEStatus, isTagged: boolean) => {
  const colorMap = {
    [OpportunitySEStatus.NoneAssigned]: 'bg-danger',
    [OpportunitySEStatus.NotRequired]: 'bg-success',
    [OpportunitySEStatus.WorkInProgress]: isTagged ? 'bg-success' : 'bg-warning-darker',
    [OpportunitySEStatus.Completed]: 'bg-success',
    [OpportunitySEStatus.TechnicalOnHold]: 'bg-gray-500',
  };
  return colorMap[status] || 'bg-gray-500';
}